// IndexedDB wrapper for offline storage
class IndexedDBService {
  constructor() {
    this.dbName = 'LoanCollectorDB';
    this.version = 4; // Incremented to add search cache store
    this.db = null;
  }

  async init() {
    return new Promise((resolve, reject) => {
      // First, try to open with the current version
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        const error = request.error;
        console.error('IndexedDB open error:', error);
        reject(error);
      };
      
      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB opened successfully, version:', this.db.version);
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        this.setupStores(event);
      };
    });
  }

  setupStores(event) {
        const db = event.target.result;
    const oldVersion = event.oldVersion;
    const newVersion = event.newVersion;

    console.log(`IndexedDB upgrade from version ${oldVersion} to ${newVersion}`);

        // Store for pending payments
        if (!db.objectStoreNames.contains('pendingPayments')) {
          const paymentStore = db.createObjectStore('pendingPayments', {
        keyPath: 'id'
          });
          paymentStore.createIndex('installmentId', 'installmentId', { unique: false });
          paymentStore.createIndex('status', 'status', { unique: false });
          paymentStore.createIndex('timestamp', 'timestamp', { unique: false });
      paymentStore.createIndex('synced', 'synced', { unique: false });
    } else if (oldVersion < 2) {
      // If upgrading from version 1, add synced index if it doesn't exist
      const transaction = event.target.transaction;
      const paymentStore = transaction.objectStore('pendingPayments');
      if (!paymentStore.indexNames.contains('synced')) {
        paymentStore.createIndex('synced', 'synced', { unique: false });
      }
        }

        // Store for loan schedules (cached data)
        if (!db.objectStoreNames.contains('loanSchedules')) {
          const loanStore = db.createObjectStore('loanSchedules', {
            keyPath: 'loanId'
          });
          loanStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
        }

        // Store for customers (cached data)
        if (!db.objectStoreNames.contains('customers')) {
          const customerStore = db.createObjectStore('customers', {
            keyPath: 'id'
          });
          customerStore.createIndex('nic', 'nic', { unique: false });
        }

        // Store for installments (cached data)
        if (!db.objectStoreNames.contains('installments')) {
          const installmentStore = db.createObjectStore('installments', {
            keyPath: 'id'
          });
          installmentStore.createIndex('loanId', 'loanId', { unique: false });
          installmentStore.createIndex('status', 'status', { unique: false });
        }

        // Store for search results (customer + loans + installments)
        if (!db.objectStoreNames.contains('searchResults')) {
          const searchStore = db.createObjectStore('searchResults', {
            keyPath: 'id'
          });
          searchStore.createIndex('nic', 'nic', { unique: false });
          searchStore.createIndex('loanNumber', 'loanNumber', { unique: false });
          searchStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
        }
  }

  // Payment operations
  async savePendingPayment(paymentData) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['pendingPayments'], 'readwrite');
      const store = transaction.objectStore('pendingPayments');

      // Ensure payment has an id (use the one from paymentData or generate)
      const payment = {
        ...paymentData,
        id: paymentData.id || `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        status: paymentData.status || 'PENDING',
        timestamp: paymentData.timestamp || new Date().toISOString(),
        synced: paymentData.synced !== undefined ? paymentData.synced : false
      };

      const request = store.put(payment); // Use put instead of add to handle updates

      request.onsuccess = () => {
        console.log('Payment saved to IndexedDB:', payment.id);
        resolve(payment);
      };

      request.onerror = () => {
        console.error('Error saving payment to IndexedDB:', request.error);
        reject(request.error);
      };
    });
  }

  async getPendingPayments() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['pendingPayments'], 'readonly');
      const store = transaction.objectStore('pendingPayments');
      
      // Get all payments and filter for pending ones
      const request = store.getAll();

      request.onsuccess = () => {
        const allPayments = request.result || [];
        // Filter for pending payments (not synced)
        const pending = allPayments.filter(p => 
          (p.status === 'PENDING' || !p.status) && !p.synced
        );
        console.log(`Found ${pending.length} pending payments out of ${allPayments.length} total`);
        resolve(pending);
      };
      
      request.onerror = () => {
        console.error('Error getting pending payments:', request.error);
        reject(request.error);
      };
    });
  }

  async getPendingPaymentByInstallmentId(installmentId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['pendingPayments'], 'readonly');
      const store = transaction.objectStore('pendingPayments');
      const index = store.index('installmentId');
      const request = index.getAll(parseInt(installmentId));

      request.onsuccess = () => {
        const allPayments = request.result || [];
        // Filter for pending payments (not synced) for this installment
        const pending = allPayments.filter(p => 
          (p.status === 'PENDING' || !p.status) && !p.synced
        );
        // Sort by timestamp (most recent first) and return the most recent pending payment if any
        if (pending.length > 0) {
          pending.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
          resolve(pending[0]);
        } else {
          resolve(null);
        }
      };
      
      request.onerror = () => {
        console.error('Error getting pending payment by installment ID:', request.error);
        reject(request.error);
      };
    });
  }

  async getAllPendingPaymentsByInstallmentId(installmentId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['pendingPayments'], 'readonly');
      const store = transaction.objectStore('pendingPayments');
      const index = store.index('installmentId');
      const request = index.getAll(parseInt(installmentId));

      request.onsuccess = () => {
        const allPayments = request.result || [];
        // Filter for pending payments (not synced) for this installment
        const pending = allPayments.filter(p => 
          (p.status === 'PENDING' || !p.status) && !p.synced
        );
        // Return all pending payments for this installment
        resolve(pending);
      };
      
      request.onerror = () => {
        console.error('Error getting pending payments by installment ID:', request.error);
        reject(request.error);
      };
    });
  }

  async getPendingPaymentIdsByInstallmentIds(installmentIds) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['pendingPayments'], 'readonly');
      const store = transaction.objectStore('pendingPayments');
      const request = store.getAll();

      request.onsuccess = () => {
        const allPayments = request.result || [];
        // Filter for pending payments (not synced) for the given installment IDs
        const pendingMap = {};
        const installmentIdSet = new Set(installmentIds.map(id => parseInt(id)));
        
        allPayments.forEach(p => {
          if (installmentIdSet.has(parseInt(p.installmentId)) && 
              (p.status === 'PENDING' || !p.status) && 
              !p.synced) {
            pendingMap[p.installmentId] = true;
          }
        });
        
        resolve(pendingMap);
      };
      
      request.onerror = () => {
        console.error('Error getting pending payments by installment IDs:', request.error);
        reject(request.error);
      };
    });
  }

  async markPaymentAsSynced(paymentId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['pendingPayments'], 'readwrite');
      const store = transaction.objectStore('pendingPayments');
      const request = store.get(paymentId);

      request.onsuccess = () => {
        const payment = request.result;
        if (payment) {
          payment.synced = true;
          payment.status = 'SYNCED';
          payment.syncedAt = new Date().toISOString();
          const updateRequest = store.put(payment);
          updateRequest.onsuccess = () => resolve(payment);
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  async deleteSyncedPayment(paymentId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['pendingPayments'], 'readwrite');
      const store = transaction.objectStore('pendingPayments');
      const request = store.delete(paymentId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Cache operations for loan schedules
  async cacheLoanSchedule(loanId, scheduleData) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['loanSchedules'], 'readwrite');
      const store = transaction.objectStore('loanSchedules');

      const data = {
        loanId,
        ...scheduleData,
        lastUpdated: new Date().toISOString()
      };

      const request = store.put(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getCachedLoanSchedule(loanId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['loanSchedules'], 'readonly');
      const store = transaction.objectStore('loanSchedules');
      const request = store.get(loanId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Cache operations for customers
  async cacheCustomers(customers) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['customers'], 'readwrite');
      const store = transaction.objectStore('customers');

      const requests = customers.map(customer => store.put(customer));
      Promise.all(requests.map(req => new Promise((res, rej) => {
        req.onsuccess = () => res();
        req.onerror = () => rej(req.error);
      })))
        .then(() => resolve())
        .catch(reject);
    });
  }

  async getCachedCustomers() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['customers'], 'readonly');
      const store = transaction.objectStore('customers');
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Cache operations for installments
  async cacheInstallments(installments) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['installments'], 'readwrite');
      const store = transaction.objectStore('installments');

      const requests = installments.map(installment => store.put(installment));
      Promise.all(requests.map(req => new Promise((res, rej) => {
        req.onsuccess = () => res();
        req.onerror = () => rej(req.error);
      })))
        .then(() => resolve())
        .catch(reject);
    });
  }

  async getCachedInstallments(loanId) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['installments'], 'readonly');
      const store = transaction.objectStore('installments');
      const index = store.index('loanId');
      const request = index.getAll(loanId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Cache operations for installments due today
  async cacheInstallmentsDueToday(installments) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['installments'], 'readwrite');
      const store = transaction.objectStore('installments');

      // Mark these as due today installments
      const installmentsToCache = installments.map(inst => ({
        ...inst,
        cachedFor: 'due-today',
        lastUpdated: new Date().toISOString()
      }));

      const requests = installmentsToCache.map(installment => store.put(installment));
      Promise.all(requests.map(req => new Promise((res, rej) => {
        req.onsuccess = () => res();
        req.onerror = () => rej(req.error);
      })))
        .then(() => {
          console.log(`Cached ${installmentsToCache.length} installments due today`);
          resolve();
        })
        .catch(reject);
    });
  }

  async getCachedInstallmentsDueToday() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['installments'], 'readonly');
      const store = transaction.objectStore('installments');
      const request = store.getAll();

      request.onsuccess = () => {
        const allInstallments = request.result || [];
        // Filter for due today installments
        const dueToday = allInstallments.filter(inst => inst.cachedFor === 'due-today');
        console.log(`Retrieved ${dueToday.length} cached installments due today`);
        resolve(dueToday);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Cache operations for active loans
  async cacheActiveLoans(loans) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['loanSchedules'], 'readwrite');
      const store = transaction.objectStore('loanSchedules');

      // Store each loan with a special key
      const requests = loans.map(loanData => {
        const cacheData = {
          loanId: `active_${loanData.loan?.id || loanData.id}`,
          ...loanData,
          cachedFor: 'active-loans',
          lastUpdated: new Date().toISOString()
        };
        return store.put(cacheData);
      });

      Promise.all(requests.map(req => new Promise((res, rej) => {
        req.onsuccess = () => res();
        req.onerror = () => rej(req.error);
      })))
        .then(() => {
          console.log(`Cached ${loans.length} active loans`);
          resolve();
        })
        .catch(reject);
    });
  }

  async getCachedActiveLoans() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['loanSchedules'], 'readonly');
      const store = transaction.objectStore('loanSchedules');
      const request = store.getAll();

      request.onsuccess = () => {
        const allData = request.result || [];
        // Filter for active loans
        const activeLoans = allData
          .filter(data => data.cachedFor === 'active-loans')
          .map(data => {
            // Remove the cache metadata
            const { cachedFor, lastUpdated, loanId, ...loanData } = data;
            return loanData;
          });
        console.log(`Retrieved ${activeLoans.length} cached active loans`);
        resolve(activeLoans);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Search cache operations
  async cacheSearchResult(searchKey, searchType, searchData) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['searchResults'], 'readwrite');
      const store = transaction.objectStore('searchResults');

      const cacheData = {
        id: `${searchType}_${searchKey}`,
        searchType: searchType, // 'nic' or 'loanNumber'
        searchKey: searchKey,
        nic: searchType === 'nic' ? searchKey : (searchData.customer?.nic || null),
        loanNumber: searchType === 'loanNumber' ? parseInt(searchKey) : null,
        data: searchData,
        lastUpdated: new Date().toISOString()
      };

      const request = store.put(cacheData);

      request.onsuccess = () => {
        console.log(`Cached search result for ${searchType}: ${searchKey}`);
        resolve();
      };

      request.onerror = () => reject(request.error);
    });
  }

  async getCachedSearchResultByNic(nic) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['searchResults'], 'readonly');
      const store = transaction.objectStore('searchResults');
      const index = store.index('nic');
      const request = index.getAll(nic);

      request.onsuccess = () => {
        const results = request.result || [];
        // Find the most recent result for this NIC
        if (results.length > 0) {
          const mostRecent = results.sort((a, b) => 
            new Date(b.lastUpdated) - new Date(a.lastUpdated)
          )[0];
          console.log(`Found cached search result for NIC: ${nic}`);
          resolve(mostRecent.data);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  async getCachedSearchResultByLoanNumber(loanNumber) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(['searchResults'], 'readonly');
      const store = transaction.objectStore('searchResults');
      const index = store.index('loanNumber');
      const request = index.getAll(parseInt(loanNumber));

      request.onsuccess = () => {
        const results = request.result || [];
        // Find the most recent result for this loan number
        if (results.length > 0) {
          const mostRecent = results.sort((a, b) => 
            new Date(b.lastUpdated) - new Date(a.lastUpdated)
          )[0];
          console.log(`Found cached search result for Loan Number: ${loanNumber}`);
          resolve(mostRecent.data);
        } else {
          resolve(null);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Clear all cached data
  async clearCache() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(
        ['loanSchedules', 'customers', 'installments', 'searchResults'],
        'readwrite'
      );

      const stores = ['loanSchedules', 'customers', 'installments', 'searchResults'];
      const clearPromises = stores.map(storeName => {
        const store = transaction.objectStore(storeName);
        return new Promise((res, rej) => {
          const request = store.clear();
          request.onsuccess = () => res();
          request.onerror = () => rej(request.error);
        });
      });

      Promise.all(clearPromises)
        .then(() => resolve())
        .catch(reject);
    });
  }
}

export default new IndexedDBService();






